import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import uuidV4 from 'uuid/v4';
import AnimationUtils from '@spothero/utils/animation';
import {window} from 'ssr-window';
import DOMUtils from '@spothero/utils/dom';
import EnvironmentUtils from '@spothero/utils/environment';
import WithConfig from 'config/WithConfig';
import includes from 'lodash/includes';
import SearchMeta from 'search/SearchMeta';
import SegmentUtils from 'utils/segment';
import GTMUtils from 'utils/gtm';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import {notify} from 'notification/notification-actions';
import {getSpotsAirport} from 'spots/spots-actions';
import {getSpotsAirportStructuredData} from 'destination/destination-actions';
import Icon from 'common/icon/Icon';
import SearchAirportControls from './SearchAirportControls';
import SearchAirportHero from './SearchAirportHero';
import SearchAirportResultsResponsive from './SearchAirportResultsResponsive';
import SearchAirportStructuredDataCard from './SearchAirportStructuredDataCard';

class SearchAirport extends PureComponent {
    static propTypes = {
        siteUrl: PropTypes.string.isRequired,
        destination: PropTypes.object,
        city: PropTypes.object,
        ends: PropTypes.string,
        hasUserInput: PropTypes.bool,
        loadAirportSpots: PropTypes.func.isRequired,
        loadAirportStructuredDataSpots: PropTypes.func.isRequired,
        starts: PropTypes.string,
        airportStructuredData: PropTypes.array,
        sendNotification: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        this._isMobile = EnvironmentUtils.isMobileDevice();
    }

    componentDidMount() {
        const {
            starts,
            ends,
            loadAirportSpots,
            loadAirportStructuredDataSpots,
            destination: {
                airport: {
                    iata_code: iataCode,
                } = {}
            }
        } = this.props;

        this._searchPageContainer = DOMUtils.el('body');

        DOMUtils.addClass(this._searchPageContainer, 'search-airport-page');

        if (starts && ends) {
            loadAirportSpots();
        }

        loadAirportStructuredDataSpots(iataCode);

        if (!includes(document.referrer, 'spothero.com')) {
            // only track this if the user is coming from an outside source not somewhere on our site
            SegmentUtils.track({
                event: 'Search Landing',
            });
        }

        SegmentUtils.track({
            event: 'Airport Landing'
        });

        GTMUtils.push({
            event: 'pageview',
            page: 'search-airport'
        });

        window.scrollTo(0, 0);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            starts: prevStarts,
            ends: prevEnds,
        } = prevProps;
        const {
            starts,
            ends,
            loadAirportSpots,
        } = this.props;

        if (prevStarts !== starts || prevEnds !== ends) {
            loadAirportSpots();
        }
    }

    componentWillUnmount() {
        DOMUtils.removeClass(this._searchPageContainer, 'search-airport-page');
    }

    _onSearchSubmit = () => {
        this._scrollToResults();
    }

    _onStructuredDataClick = () => {
        const {
            sendNotification
        } = this.props;
        const topPos = (this._isMobile)
            ? DOMUtils.getDocumentOffset('.AirportDateTimePickers').top - 52
            : 0;

        AnimationUtils.scrollTo('html', topPos);

        sendNotification({
            id: uuidV4(),
            type: 'info',
            message: 'Please search to check availability.'
        });
    }

    _scrollToResults = () => {
        const airportContentTop = DOMUtils.getDocumentOffset('.SearchAirport-content').top;
        const topPos = (this._isMobile)
            ? airportContentTop + 310 - 52 // height of datepickers - height of nav
            : airportContentTop + 64;

        AnimationUtils.scrollTo('html', topPos);
    }

    render() {
        const {
            destination,
            city,
            hasUserInput,
            airportStructuredData,
            siteUrl,
        } = this.props;
        const {
            relative_url: destinationRelativeUrl,
        } = destination;
        const {
            slug: citySlug
        } = city;

        return (
            <div className="SearchAirport">
                <SearchMeta />
                <SearchAirportHero
                    destination={destination}
                    city={city}
                    isMobile={this._isMobile}
                />
                <div className="SearchAirport-content">
                    <div className="SearchAirport-container">
                        <SearchAirportControls
                            onSubmit={this._onSearchSubmit}
                        />
                        <span className="SearchAirport-tagline">
                            <Icon name="check-circle" /> Plans change? Cancel up to your start time for a full refund.
                        </span>
                    </div>
                    {hasUserInput && <SearchAirportResultsResponsive />}
                </div>
                {Boolean(destination.description) &&
                    <div
                        className="SearchAirport-container SearchAirport-description"
                        dangerouslySetInnerHTML={{__html: destination.description}}
                    />
                }
                {(airportStructuredData && airportStructuredData.length > 0) &&
                    <div className="SearchAirport-container SearchAirport-structured-data">
                        <h2>Top Rated Parking Near {destination.title}</h2>
                        <p>Input your dates above to see the full list of parking options available through SpotHero.</p>
                        <ul
                            className="SearchAirport-structured-data-spots"
                            itemScope
                            itemType="http://schema.org/ItemList"
                        >
                            <link
                                itemProp="url"
                                href={`${siteUrl}/${citySlug}/${destinationRelativeUrl}`}
                            />
                            <meta
                                itemProp="numberOfItems"
                                content={airportStructuredData.length}
                            />
                            {airportStructuredData.map((spot, index) => {
                                return (
                                    <li
                                        className="SearchAirport-structured-data-spot-wrapper"
                                        key={spot.facility.id}
                                        itemProp="itemListElement"
                                        itemScope
                                        itemType="http://schema.org/ListItem"
                                    >
                                        <meta
                                            itemProp="position"
                                            content={index + 1}
                                        />
                                        <SearchAirportStructuredDataCard
                                            key={spot.facility.id}
                                            spot={spot}
                                            destinationUrl={`${siteUrl}/${citySlug}/${destinationRelativeUrl}`}
                                            onStructuredDataClick={this._onStructuredDataClick}
                                        />
                                    </li>
                                );
                            })
                            }
                        </ul>
                    </div>
                }
            </div>
        );
    }
}

const makeMapStateToProps = () => {
    const mapStateToProps = state => {
        const {
            city: {
                data: city
            },
            destination: {
                data: destination,
                airportStructuredData
            },
            searchRequest: {
                starts,
                ends
            }
        } = state;

        return {
            destination,
            city,
            starts,
            ends,
            airportStructuredData,
            hasUserInput: Boolean(starts && ends),
        };
    };

    return mapStateToProps;
};

const mapDispatchToProps = {
    loadAirportSpots: getSpotsAirport,
    loadAirportStructuredDataSpots: getSpotsAirportStructuredData,
    sendNotification: notify
};

const enhance = compose(
    WithConfig,
    connect(makeMapStateToProps, mapDispatchToProps)
);

export default enhance(SearchAirport);
